import React, { useState, useEffect, useRef, useContext } from 'react';
import ListLoadError from '../../components/ListLoadError';
import EntityScopeNotice from '../../components/Invoicing/EntityScopeNotice';
import FinancialsEmptyState from '../../components/Invoicing/FinancialsEmptyState';
import { extractApiError } from '../../utils/errorMessages';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReceipt, faPlus, faEye, faCheckCircle, faClock, faExclamationTriangle, faMoneyBillWave, faArrowLeft, faSync, faEdit, faTrash, faClipboardList, faFileUpload, faFilePdf, faProjectDiagram, faBan, faMoneyCheckAlt } from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import logo from '../../assets/images/IMT_logo.png';
import Footer from '../../components/ui/Footer';
import invoicingService, {
  deliveryScopedListNotice, deliveryScopedEmptyNotice,
  deliveryScopedListHeading, DELIVERY_SCOPED_NOTICE_STYLE,
} from '../../services/invoicingService';
import { AuthContext } from '../../context/AuthContext';
import { hasFinancialAccess, isDeliveryScopedInvoiceReader } from '../../utils/financialAccess';
import { isRestrictedCustomerTrader } from '../../utils/customerTraderAccess';
import InvoiceAuditModal from '../../components/Invoicing/InvoiceAuditModal';
import InvoiceSearchFilter from '../../components/Invoicing/InvoiceSearchFilter';
import InvoiceRelationshipModal from '../../components/Invoicing/InvoiceRelationshipModal';
import InvoiceCaptureModal from '../../components/Invoicing/InvoiceCaptureModal';
import TradeOpsDeleteModal from '../../components/TradeOpsDeleteModal';
import SupplierPdfChooserModal from '../../components/shared/SupplierPdfChooserModal';
import VoidInvoiceModal from './VoidInvoiceModal';
import './OutgoingInvoice.css';
import SortableTh from '../../components/shared/SortableTh';
import useTableSort from '../../components/shared/useTableSort';
import useSavedRowFlash from '../../components/shared/useSavedRowFlash';
import SavedRowBanner from '../../components/shared/SavedRowBanner';
import { INCOMING_INVOICE_COLUMNS, incomingPaymentStatus, partPaidSummary } from './listColumns';

/* What this screen actually shows. The delivery-scoped banner used to be
 * hard-coded "Showing your deliveries' invoices." on all four list screens,
 * so an operator on this one was told the wrong noun about the very list they
 * were being asked to trust. */
const SCOPED_DOCUMENT_PLURAL = 'incoming invoices';

// Statuses at which an issued invoice is "live" and can be voided
const VOIDABLE_STATUSES = ['APPROVED', 'SENT', 'PAID', 'OVERDUE'];

/**
 * Incoming Invoice List Page (Accounts Payable)
 * 
 * Features:
 * - List all incoming/supplier invoices
 * - Filter by status, supplier, payment status
 * - Search by invoice number or supplier name
 * - Create new incoming invoice (manual entry)
 * - View payment details
 * - Track overdue payments
 */


const IncomingInvoiceList = () => {
  const history = useHistory();
  // WHICH READER IS THIS?
  // Two very different people share this screen since 2026-09-07. A finance
  // user reads their whole entity book. A trade-ops operator reads only the
  // invoices raised against deliveries they can already open — the server
  // clamps the rows (invoicing.delivery_scoped_access) and this flag makes the
  // SCREEN honest about it: every control below that the server would refuse is
  // hidden rather than left to fail. A refused call rendering as an empty panel
  // is the fault this codebase keeps re-learning.
  //
  // The predicate is the shared one in utils/financialAccess — the same rule the
  // backend applies. An unresolved user (still loading) is neither reader, so
  // the screen behaves exactly as it did before this change until the answer
  // arrives, and the server stays the only real enforcer.
  const { user } = useContext(AuthContext);
  const scopedReader = isDeliveryScopedInvoiceReader(user);
  // A customer trader is an EXTERNAL counterparty who happens to hold
  // has_bunkers_access, so the route gate lets them through the door. They must
  // never read our invoice book: the server refuses them outright, and this
  // stops the screen asking on their behalf and then drawing a bare table.
  const barredTrader = Boolean(user) && isRestrictedCustomerTrader(user) && !hasFinancialAccess(user);
  const [invoices, setInvoices] = useState([]);
  // Column sorting — comparators live in utils/tableSort, column kinds in
  // ./listColumns, so all six invoicing lists sort by the same rules.
  const { sort, onSort, applySort } = useTableSort(INCOMING_INVOICE_COLUMNS);
  const [loading, setLoading] = useState(true);
  // Refusal or failure text for the banner. Without it a 403 renders as
  // an empty table, which reads as "you have none" rather than "we could
  // not ask".
  const [loadError, setLoadError] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  
  // Search and filter state
  const [searchResults, setSearchResults] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // Audit modal state
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedInvoiceForAudit, setSelectedInvoiceForAudit] = useState(null);
  
  // Relationship modal state
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [selectedInvoiceForRelationship, setSelectedInvoiceForRelationship] = useState(null);
  
  // Capture modal state
  const [isCaptureModalOpen, setIsCaptureModalOpen] = useState(false);
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Supplier PDF preview modal state
  const [showSupplierPdfModal, setShowSupplierPdfModal] = useState(false);
  const [supplierPdfInvoiceId, setSupplierPdfInvoiceId] = useState(null);

  // Void modal state
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [invoiceToVoid, setInvoiceToVoid] = useState(null);
  const [isVoiding, setIsVoiding] = useState(false);

  // Component-level flag to prevent double load (resets on unmount)
  const hasLoadedRef = useRef(false);
  
  useEffect(() => {
    // `barredTrader` is false while the profile is still resolving, so a first
    // pass can fetch before we know who is asking. Re-running on that flag
    // means the refusal lands the moment the answer arrives, instead of a
    // customer trader keeping a table they should never have been shown.
    if (barredTrader) {
      setInvoices([]);
      loadInvoices();   // takes the refusal branch, makes no request
      return;
    }
    // Load invoices only once on initial mount
    if (!isSearchMode && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchMode, barredTrader]);
  
  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadInvoices = async () => {
    // Say no here rather than let the server say it. An external counterparty
    // asking for our accounts payable is not a load failure to retry, it is an
    // answer — and the retry button on a refusal banner invites them to keep
    // asking.
    if (barredTrader) {
      setLoading(false);
      setLoadError(
        'Incoming invoices are internal to IMT. Your account is a customer '
        + 'trader account, which can see its own deliveries but not our invoice '
        + 'book. Contact your IMT representative if you need an invoice.'
      );
      return;
    }
    try {
      setLoading(true);
      setLoadError(null);
      const data = await invoicingService.fetchIncomingInvoices({});
      setInvoices(data.results || data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading incoming invoices:', error);
      setLoadError(extractApiError(error, 'The incoming invoices could not be loaded.'));
      setLoading(false);
    }
  };

  const handleSearchResults = (results) => {
    console.log('handleSearchResults called with', results.length, 'results');
    setSearchResults(results);
    
    // Enter search mode if we have results or active filters
    if (results.length > 0 || Object.keys(activeFilters).some(key => activeFilters[key])) {
      console.log('ENTERING SEARCH MODE - we have results or filters');
      setIsSearchMode(true);
    } else {
      console.log('NO RESULTS AND NO FILTERS - staying in search mode but showing empty');
      setIsSearchMode(true);
    }
  };

  const handleFiltersChange = (filters) => {
    setActiveFilters(filters);
    const hasFilters = Object.keys(filters).some(key => filters[key]);
    if (hasFilters) {
      setIsSearchMode(true);
    }
  };

  const handleSearchStateChange = (searching) => {
    setIsSearching(searching);
  };

  const handleClearSearch = () => {
    console.log('handleClearSearch() CALLED - EXITING SEARCH MODE AND LOADING ALL ITEMS');
    setIsSearchMode(false);
    setSearchResults([]);
    setActiveFilters({});
    loadInvoices();
  };
  
  const getStatusIcon = (status) => {
    switch(status) {
      case 'DRAFT': return null;
      case 'PENDING_APPROVAL': return null;
      case 'APPROVED': return <FontAwesomeIcon icon={faCheckCircle} className="status-icon approved" />;
      case 'PAID': return <FontAwesomeIcon icon={faCheckCircle} className="status-icon paid" />;
      case 'OVERDUE': return <FontAwesomeIcon icon={faExclamationTriangle} className="status-icon overdue" />;
      case 'CANCELLED': return <FontAwesomeIcon icon={faExclamationTriangle} className="status-icon cancelled" />;
      default: return null;
    }
  };
  
  // One derivation for the badge AND the column sort — if they drifted apart,
  // the list would sort by something other than what it shows.
  const getPaymentStatus = (invoice) => {
    const state = incomingPaymentStatus(invoice);
    return (
      <span
        className={`payment-badge ${state.toLowerCase().replace(/\s+/g, '-')}`}
        title={state === 'Partially Paid' ? partPaidSummary(invoice) : undefined}
      >
        {state}
      </span>
    );
  };
  
  const formatCurrency = (amount, currency) => {
    if (!amount || isNaN(amount)) return '0.00';
    return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  
  const formatReportingMonth = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };
  
  const toggleDropdown = (invoiceId) => {
    setActiveDropdown(activeDropdown === invoiceId ? null : invoiceId);
  };
  
  const handleEdit = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    history.push(`/invoicing/incoming/edit/${invoice.id}`);
  };
  
  const handleDelete = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setItemToDelete({
      id: invoice.id,
      type: 'invoice',
      details: {
        invoiceNumber: invoice.hms_expense_number || 'DRAFT',
        supplierName: invoice.partner_name || 'N/A',
        grossAmount: invoice.gross_amount 
          ? `${invoice.currency || 'USD'} ${parseFloat(invoice.gross_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : null,
        createdAt: invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'
      }
    });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await invoicingService.deleteIncomingInvoice(itemToDelete.id);
      loadInvoices();
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Failed to delete invoice. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleVoid = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setInvoiceToVoid(invoice);
    setShowVoidModal(true);
  };

  const handleVoidCancel = () => {
    if (isVoiding) return;
    setShowVoidModal(false);
    setInvoiceToVoid(null);
  };

  const handleVoidConfirm = async () => {
    if (!invoiceToVoid) return;
    setIsVoiding(true);
    try {
      const result = await invoicingService.voidIncomingInvoice(invoiceToVoid.id);
      setShowVoidModal(false);
      setInvoiceToVoid(null);
      alert(result?.message || 'Invoice voided and debit note created.');
      loadInvoices();
    } catch (error) {
      console.error('Error voiding invoice:', error);
      alert(extractApiError(error, 'Failed to void invoice. Please try again.'));
    } finally {
      setIsVoiding(false);
    }
  };
  
  const handleViewGlobalAudit = () => {
    setSelectedInvoiceForAudit(null);
    setShowAuditModal(true);
  };
  
  const handleViewInvoiceAudit = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setSelectedInvoiceForAudit(invoice);
    setShowAuditModal(true);
  };
  
  const handleCloseAuditModal = () => {
    setShowAuditModal(false);
    setSelectedInvoiceForAudit(null);
  };
  
  const handleViewRelationships = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setSelectedInvoiceForRelationship(invoice);
    setShowRelationshipModal(true);
  };

  const handlePreviewSupplierPdf = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setSupplierPdfInvoiceId(invoice.id);
    setShowSupplierPdfModal(true);
  };

  // Sorted wherever the rows came from — the loaded list OR the search
  // results — so the table never behaves differently in the two modes.
  const visibleRows = applySort(isSearchMode ? searchResults : invoices);
  // Point the user at the row they just saved. The creators hand the id
  // over as route state; without it the toast names a row this table
  // gives no way to find (ordered by document date, not by recency).
  const { savedId, rowRef } = useSavedRowFlash({ sort, isSearchMode, activeFilters });
  const savedRowClass = (id) => (savedId !== null && String(id) === String(savedId) ? 'row-just-saved' : '');
  // The banner speaks only about a row that is actually on this page. If the
  // saved row was filtered out or fell beyond the fetched page, saying "see
  // below" would point at nothing -- the toast stands alone instead.
  // A DRAFT has no number yet, so the label falls back to generic wording
  // rather than printing 'undefined' at the user.
  const savedRow = savedId === null
    ? null
    : visibleRows.find(r => String(r.id) === String(savedId)) || null;
  const savedRowLabel = savedRow?.hms_expense_number || null;

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container incoming-invoice-screen">
        {/* Header */}
        <div className="hms-screen-header">
          {/* Back Button */}
          {/* The Invoicing dashboard is an aggregate over the WHOLE book and
              stays finance-only, so sending an operator there is sending them
              straight back into the "You don't have permission to access
              Financials" bounce this change exists to remove. The main dashboard
              is the one destination every reader of this screen can open —
              bunkers, cargo or finance. */}
          <button
            className="invoice-header-back-button"
            onClick={() => history.push(scopedReader ? '/dashboard' : '/invoicing')}
            title={scopedReader ? 'Back to Dashboard' : 'Back to Invoicing Dashboard'}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>

          <div className="header-logo">
            <img src={logo} alt="IMT Bergbau AG" className="header-logo-img" />
            <div className="header-title-content">
              <h1 className="hms-screen-title">INCOMING INVOICES</h1>
              <p className="invoice-subtitle">Supplier invoices and accounts payable</p>
            </div>
          </div>
          
          <div className="header-actions">
            {/* Audit Center reads `audit_logs`, which is NOT in the delivery-
                scoped whitelist — it is a history of the whole book. An operator
                pressing it gets a 403 the modal would render as "no entries",
                i.e. a lie. Hidden rather than left to fail. */}
            {!scopedReader && (
              <button className="btn-secondary" onClick={handleViewGlobalAudit}>
                <FontAwesomeIcon icon={faClipboardList} /> Audit Center
              </button>
            )}

            {/* Payment Run is Finance's: the server refuses every payment-run door
                to operators, so the button is not offered to them. */}
            {!scopedReader && !barredTrader && (
              <button className="btn-secondary" onClick={() => history.push('/invoicing/payment-runs')}>
                <FontAwesomeIcon icon={faMoneyCheckAlt} /> Payment Run
              </button>
            )}

            {/* Capture stays for everyone: `upload_and_capture` reads a file the
                user has just handed us and touches no invoice, so the scoped
                tier is allowed it. */}
            {!barredTrader && (
              <button className="btn-secondary" onClick={() => setIsCaptureModalOpen(true)}>
                <FontAwesomeIcon icon={faFileUpload} /> Capture Invoice
              </button>
            )}

            {!barredTrader && (
              <button className="btn-primary" onClick={() => history.push('/invoicing/incoming/create')}>
                <FontAwesomeIcon icon={faPlus} /> Create New Invoice
              </button>
            )}
          </div>
        </div>
        
        {/* Search and Filter Controls */}
        {/* The search bar drives the SAME `list` action with query params, which
            the scoped tier is allowed and the server clamps identically — so it
            works for an operator and stays mounted. It is not rendered at
            all for a barred customer trader, who has no list to search — and not
            merely `hidden`, because `.search-filter-controls` sets `display: flex`,
            which wins over that attribute. */}
        {!barredTrader && (
        <div className="search-filter-controls">
          <div className="search-filter-wrapper">
            <InvoiceSearchFilter
              onSearchResults={handleSearchResults}
              onFiltersChange={handleFiltersChange}
              isSearching={isSearching}
              onSearchStateChange={handleSearchStateChange}
              onClearSearch={handleClearSearch}
              invoiceType="incoming"
            />
          </div>
          
          <button 
            className="reload-button"
            onClick={loadInvoices}
            title="Reload invoices"
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>
        )}
        
        {/* Invoice List */}
        <div className="invoice-list-container">
          <ListLoadError message={loadError} onRetry={loadInvoices} />
          {/* Says which entity's book this list is showing. Without it a
              narrowed list reads as "nothing has been raised" — the fault
              reported on 2026-09-03.
              An operator is narrowed by DELIVERY, not by entity (the scoped tier
              skips entity scoping on purpose), so the entity sentence would be a
              plain untruth for them. They get the rule that is actually in
              force. */}
          {scopedReader ? (
            <div role="status" style={DELIVERY_SCOPED_NOTICE_STYLE}>
              <strong style={{ marginRight: '0.4rem' }}>
                {deliveryScopedListHeading(SCOPED_DOCUMENT_PLURAL)}
              </strong>
              {deliveryScopedListNotice(SCOPED_DOCUMENT_PLURAL)}
            </div>
          ) : (
            <EntityScopeNotice emptyResult={!loading && visibleRows.length === 0} />
          )}
          <SavedRowBanner savedId={savedRow ? savedId : null} label={savedRowLabel} />
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading incoming invoices...</p>
            </div>
          ) : (
            <table className="invoice-list-table">
              <thead>
                <tr>
                  {INCOMING_INVOICE_COLUMNS.map((column) => (
                    <SortableTh key={column.key} column={column} sort={sort} onSort={onSort} />
                  ))}
                  {/* Actions holds buttons, not data — nothing to sort by. */}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="empty-state-cell">
                      <div className="empty-state-container">
                        <div className="empty-state-icon">
                          <FontAwesomeIcon icon={faReceipt} />
                        </div>
                        {/* FinancialsEmptyState explains an ENTITY narrowing.
                            An operator is narrowed by delivery instead, so it
                            would name the wrong reason in the largest text on
                            the screen. */}
                        {scopedReader ? (
                          <div className="empty-state-text">
                            <h3>Nothing on your deliveries yet</h3>
                            <p>{deliveryScopedEmptyNotice(SCOPED_DOCUMENT_PLURAL)}</p>
                          </div>
                        ) : (
                          <FinancialsEmptyState
                            label="incoming invoices"
                            heading="No Incoming Invoices Yet"
                            blurb="Supplier invoices will appear here"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  visibleRows.map(invoice => (
                  <tr 
                    key={invoice.id} 
                    ref={rowRef(invoice.id)}
                    className={[invoice.status === 'OVERDUE' ? 'row-overdue' : '', savedRowClass(invoice.id)].filter(Boolean).join(' ')}
                    onClick={() => history.push(`/invoicing/incoming/${invoice.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="invoice-number-cell">{invoice.hms_expense_number || (invoice.status === 'DRAFT' ? 'DRAFT' : 'N/A')}</td>
                    <td>{invoice.supplier_invoice_number || 'N/A'}</td>
                    <td>{invoice.hms_entity_name || 'N/A'}</td>
                    <td>{invoice.partner_name || 'N/A'}</td>
                    <td>{invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}</td>
                    <td>{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}</td>
                    <td>{formatReportingMonth(invoice.reporting_month)}</td>
                    <td>{invoice.currency || 'USD'}</td>
                    <td className="amount-cell">{formatCurrency(invoice.net_amount, invoice.currency)}</td>
                    <td className="amount-cell">{formatCurrency(invoice.vat_amount, invoice.currency)}</td>
                    <td className="amount-cell">{formatCurrency(invoice.gross_amount, invoice.currency)}</td>
                    <td className="status-cell">
                      {getStatusIcon(invoice.status)}
                      <span className={`status-badge ${invoice.status.toLowerCase()}`}>
                        {invoice.status_display || invoice.status}
                        {invoice.has_adjustments && (
                          <span className="adjustment-notes-hover-container">
                            <FontAwesomeIcon 
                              icon={faExclamationTriangle} 
                              className="adjustment-icon-indicator"
                              title="This invoice has applied adjustments"
                            />
                            <div className="adjustment-notes-dropdown">
                              <div className="adjustment-notes-dropdown-header">
                                Applied Adjustments
                              </div>
                              {invoice.total_debits_applied > 0 && (
                                <div className="adjustment-note-item debit">
                                  <span className="adjustment-note-label">Debits Applied:</span>
                                  <span className="adjustment-note-amount">{formatCurrency(invoice.total_debits_applied, invoice.currency)}</span>
                                </div>
                              )}
                              <div className="adjustment-notes-dropdown-footer">
                                Original: {formatCurrency(invoice.gross_amount, invoice.currency)} | 
                                Adjusted: {formatCurrency(invoice.gross_amount - (invoice.total_debits_applied || 0), invoice.currency)}
                              </div>
                            </div>
                          </span>
                        )}
                      </span>
                    </td>
                    <td>
                      {getPaymentStatus(invoice)}
                    </td>
                    <td className="actions-cell">
                      <div 
                        className={`action-dropdown ${activeDropdown === invoice.id ? 'dropdown-open' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        ref={activeDropdown === invoice.id ? dropdownRef : null}
                      >
                        <button 
                          className="gear-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown(invoice.id);
                          }}
                        >
                          Actions
                        </button>
                        {activeDropdown === invoice.id && (
                          <div className="dropdown-menu">
                            {invoice.status !== 'VOIDED' && (
                              <button onClick={(e) => handleEdit(invoice, e)}>
                                <FontAwesomeIcon icon={faEdit} /> Edit
                              </button>
                            )}

                            {/* Everything from here down calls something the
                                delivery-scoped tier is refused, so for an
                                operator these buttons could only ever produce a
                                403 — and three of the four render a refusal as
                                an empty panel or a failed alert. They are hidden
                                for that reader and untouched for Finance.
                                  · View Relationships  -> get_relationships
                                  · Preview Supplier PDF -> DocuWare document list
                                    (docuwareApi is HasFinancialAccess-only)
                                  · View Audit Log      -> audit-logs
                                  · Void / Delete       -> money + destroy */}
                            {!scopedReader && (
                              <>
                                {/* View Relationships */}
                                <button onClick={(e) => handleViewRelationships(invoice, e)}>
                                  <FontAwesomeIcon icon={faProjectDiagram} /> View Relationships
                                </button>

                                {/* Preview Supplier PDF (from Docuware) */}
                                <button onClick={(e) => handlePreviewSupplierPdf(invoice, e)}>
                                  <FontAwesomeIcon icon={faFilePdf} /> Preview Supplier PDF
                                </button>

                                <button onClick={(e) => handleViewInvoiceAudit(invoice, e)}>
                                  <FontAwesomeIcon icon={faClipboardList} /> View Audit Log
                                </button>

                                {/* Void — creates a reversing debit note; live invoices only */}
                                {VOIDABLE_STATUSES.includes(invoice.status) && (
                                  <>
                                    <hr />
                                    <button className="danger" onClick={(e) => handleVoid(invoice, e)}>
                                      <FontAwesomeIcon icon={faBan} /> Void Invoice
                                    </button>
                                  </>
                                )}

                                {invoice.status !== 'VOIDED' && (
                                  <>
                                    <hr />
                                    <button className="danger" onClick={(e) => handleDelete(invoice, e)}>
                                      <FontAwesomeIcon icon={faTrash} /> Delete
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        
        {/* Footer */}
        <Footer />
      </div>
      
      {/* Audit Modal */}
      <InvoiceAuditModal
        isOpen={showAuditModal}
        onClose={handleCloseAuditModal}
        invoiceType="incoming"
        invoiceId={selectedInvoiceForAudit?.id || null}
        invoiceNumber={selectedInvoiceForAudit?.hms_expense_number || null}
      />
      
      {/* Relationship Modal */}
      <InvoiceRelationshipModal
        isOpen={showRelationshipModal}
        onClose={() => setShowRelationshipModal(false)}
        invoice={selectedInvoiceForRelationship}
        invoiceType="incoming"
      />
      
      {/* Invoice Capture Modal */}
      <InvoiceCaptureModal
        isOpen={isCaptureModalOpen}
        onClose={() => setIsCaptureModalOpen(false)}
        onSuccess={({ selectedData, analysisId, crmPartner, attachedOnly }) => {
          setIsCaptureModalOpen(false);
          history.push('/invoicing/incoming/create', {
            preFilledData: selectedData,
            analysisId: analysisId,
            crmPartner: crmPartner || null,
            attachedOnly: Boolean(attachedOnly),
          });
        }}
      />
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <TradeOpsDeleteModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setItemToDelete(null);
          }}
          onConfirm={confirmDelete}
          itemType={itemToDelete.type}
          itemDetails={itemToDelete.details}
          isDeleting={isDeleting}
        />
      )}

      {/* Supplier PDF Preview Modal */}
      <SupplierPdfChooserModal
        isOpen={showSupplierPdfModal}
        invoiceId={supplierPdfInvoiceId}
        onClose={() => {
          setShowSupplierPdfModal(false);
          setSupplierPdfInvoiceId(null);
        }}
        showError={(msg) => alert(msg)}
      />

      <VoidInvoiceModal
        isOpen={showVoidModal}
        invoice={invoiceToVoid}
        noteType="debit"
        isProcessing={isVoiding}
        onConfirm={handleVoidConfirm}
        onCancel={handleVoidCancel}
      />
    </Layout>
  );
};

export default IncomingInvoiceList;

